import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import NotificationCard from './components/NotificationCard';
import NotificationFilters from './components/NotificationFilters';
import NotificationStats from './components/NotificationStats';
import { supabase } from '../../lib/supabase';

const UnifiedNotificationCenter = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [filters, setFilters] = useState({
    type: 'all',
    project: 'all',
    status: 'all',
    search: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();

    // Real-time subscription for new notifications
    const channel = supabase
      ?.channel('notifications_realtime')
      ?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications'
      }, (payload) => {
        if (payload?.new) {
          setNotifications(prev => [mapNotification(payload?.new), ...prev]);
        }
      })
      ?.subscribe();

    return () => supabase?.removeChannel(channel);
  }, []);

  const mapNotification = (row) => ({
    id: row?.id,
    type: row?.type,
    title: row?.title,
    message: row?.message,
    projectName: row?.project_name,
    projectId: row?.project_id,
    vendorName: row?.vendor_name,
    amount: row?.amount,
    timestamp: row?.created_at,
    isRead: row?.is_read,
    priority: row?.priority || 'medium',
    actionUrl: row?.action_url
  });

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        ?.from('notifications')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(100);

      if (error) throw error;
      setNotifications(data?.map(mapNotification) || []);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredNotifications = () => {
    return notifications?.filter(notification => {
      const matchesType = filters?.type === 'all' || notification?.type === filters?.type;
      const matchesStatus = filters?.status === 'all' || 
        (filters?.status === 'unread' && !notification?.isRead) ||
        (filters?.status === 'read' && notification?.isRead);
      const matchesSearch = !filters?.search || 
        notification?.title?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        notification?.message?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        notification?.projectName?.toLowerCase()?.includes(filters?.search?.toLowerCase());
      
      return matchesType && matchesStatus && matchesSearch;
    });
  };

  const handleMarkAsRead = async (notificationId) => {
    setNotifications(prev => 
      prev?.map(n => n?.id === notificationId ? { ...n, isRead: true } : n)
    );
    await supabase
      ?.from('notifications')
      ?.update({ is_read: true })
      ?.eq('id', notificationId);
  };

  const handleMarkAllAsRead = async () => {
    setNotifications(prev => prev?.map(n => ({ ...n, isRead: true })));
    const { data: { user } } = await supabase?.auth?.getUser();
    if (user) {
      await supabase
        ?.from('notifications')
        ?.update({ is_read: true })
        ?.eq('user_id', user?.id)
        ?.eq('is_read', false);
    }
  };

  const handleNotificationClick = (notification) => {
    handleMarkAsRead(notification?.id);
    if (notification?.actionUrl) {
      navigate(notification?.actionUrl);
    }
  };

  const filteredNotifications = getFilteredNotifications();
  const unreadCount = notifications?.filter(n => !n?.isRead)?.length;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Bell" size={24} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-3xl font-semibold text-foreground">Notifications</h1>
                  <p className="text-text-secondary text-sm mt-1">
                    Stay updated with all your project activities
                  </p>
                </div>
              </div>
              {unreadCount > 0 && (
                <Button
                  variant="outline"
                  onClick={handleMarkAllAsRead}
                  iconName="CheckCheck"
                  iconSize={16}
                >
                  Mark All as Read
                </Button>
              )}
            </div>
          </div>

          {/* Stats */}
          <NotificationStats notifications={notifications} />

          {/* Filters */}
          <NotificationFilters 
            filters={filters}
            onFilterChange={setFilters}
            notificationCounts={{
              all: notifications?.length,
              quote: notifications?.filter(n => n?.type === 'quote')?.length,
              milestone: notifications?.filter(n => n?.type === 'milestone')?.length,
              message: notifications?.filter(n => n?.type === 'message')?.length,
              vendor: notifications?.filter(n => n?.type === 'vendor')?.length,
              unread: unreadCount,
              read: notifications?.length - unreadCount
            }}
          />

          {/* Notification Feed */}
          <div className="space-y-3">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : filteredNotifications?.length > 0 ? (
              filteredNotifications?.map(notification => (
                <NotificationCard
                  key={notification?.id}
                  notification={notification}
                  onMarkAsRead={handleMarkAsRead}
                  onClick={() => handleNotificationClick(notification)}
                />
              ))
            ) : (
              <div className="bg-card border border-border rounded-lg p-12 text-center">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Bell" size={32} className="text-text-secondary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">No Notifications Found</h3>
                <p className="text-text-secondary">
                  {filters?.type !== 'all' || filters?.status !== 'all' || filters?.search ?'Try adjusting your filters to see more notifications' :'You\'re all caught up! New notifications will appear here when vendors submit quotes.'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnifiedNotificationCenter;